import type { DatabookPreviewOutlineGroup, DatabookSheetListItem, DatabookSheetPreview } from "@/lib/api/databook";

export const DEMO_DATABOOK_ROOT = "/demo-databook/stallion";
export const DEMO_DATABOOK_DOWNLOAD = `${DEMO_DATABOOK_ROOT}/Stallion_Demo.xlsx`;

export interface DemoDatabookManifest {
  filename: string;
  sha256: string;
  sheets: string[];
  items: DatabookSheetListItem[];
  alert_sheets: string[];
  previews: Record<string, { base?: string; full?: string; pages?: Record<string, string> }>;
}

export async function loadDemoDatabookManifest(signal?: AbortSignal): Promise<DemoDatabookManifest> {
  const response = await fetch(`${DEMO_DATABOOK_ROOT}/manifest.json`, { signal, cache: "no-store" });
  if (!response.ok) throw new Error("Could not load the demo databook.");
  return response.json();
}

export async function loadDemoDatabookSheet(path: string, revision: string, signal?: AbortSignal): Promise<DatabookSheetPreview> {
  const response = await fetch(`${DEMO_DATABOOK_ROOT}/${path}?v=${encodeURIComponent(revision)}`, { signal });
  if (!response.ok || !response.body) throw new Error("Could not load the demo worksheet.");
  return new Response(response.body.pipeThrough(new DecompressionStream("gzip"))).json();
}

// Same projection as dilly_be's project_expanded_outline_groups. Coordinates and
// group boundaries come from the production reader, not from demo sheet names.
export function projectDemoDatabookSheet(
  sheet: DatabookSheetPreview,
  expandedRows: readonly string[],
  expandedColumns: readonly string[],
): DatabookSheetPreview {
  const rowIds = new Set(expandedRows);
  const columnIds = new Set(expandedColumns);
  const rowMap = new Map<number, number>();
  const columnMap = new Map<number, number>();
  const contains = (group: DatabookPreviewOutlineGroup, sourceIndex: number) =>
    group.start_source_index <= sourceIndex && sourceIndex <= group.end_source_index;
  const rows = sheet.rows.filter(row => {
    const groups = sheet.outline_groups.filter(group => group.level <= row.outline_level && contains(group, row.source_index));
    return row.outline_level === 0 || (groups.length === row.outline_level && groups.every(group => rowIds.has(group.id)));
  }).map((row, index) => {
    rowMap.set(row.index, index);
    return { ...row, index };
  });
  const columns = sheet.columns.filter(column =>
    sheet.column_outline_groups.filter(group => contains(group, column.source_index)).every(group => columnIds.has(group.id)),
  ).map((column, index) => {
    columnMap.set(column.index, index);
    return { ...column, index };
  });
  const cells = sheet.cells.filter(cell => rowMap.has(cell.row) && columnMap.has(cell.column))
    .map(cell => ({ ...cell, row: rowMap.get(cell.row)!, column: columnMap.get(cell.column)! }));
  const merges = sheet.merges.filter(merge => {
    for (let row = merge.start_row; row <= merge.end_row; row++) if (!rowMap.has(row)) return false;
    for (let column = merge.start_column; column <= merge.end_column; column++) if (!columnMap.has(column)) return false;
    return true;
  }).map(merge => ({
    start_row: rowMap.get(merge.start_row)!, end_row: rowMap.get(merge.end_row)!,
    start_column: columnMap.get(merge.start_column)!, end_column: columnMap.get(merge.end_column)!,
  }));
  const frozenRow = sheet.rows[sheet.frozen_rows - 1]?.source_index;
  const frozenColumn = sheet.columns[sheet.frozen_columns - 1]?.source_index;
  return {
    ...sheet, rows, columns, cells, merges,
    row_count: rows.length, column_count: columns.length,
    outline_level: Math.max(0, ...sheet.outline_groups.filter(group => rowIds.has(group.id)).map(group => group.level)),
    frozen_rows: frozenRow === undefined ? 0 : rows.filter(row => row.source_index <= frozenRow).length,
    frozen_columns: frozenColumn === undefined ? 0 : columns.filter(column => column.source_index <= frozenColumn).length,
    expanded_outline_groups: sheet.outline_groups.filter(group => rowIds.has(group.id)).map(group => group.id),
    expanded_column_outline_groups: sheet.column_outline_groups.filter(group => columnIds.has(group.id)).map(group => group.id),
  };
}
